/**
 * Where an account stands: exposure (what it posts) x standing (its replying
 * audience). The position is computed by the backend
 * (services/standing/position.js) and stored on the account; this file only
 * says how each position reads and looks. Mirrors the thresholds so the
 * legend can state them.
 */

export const POSITION_META = {
	solid: {
		label: 'Solid',
		meaning: 'Little to worry about in what they post, and the people replying are with them.',
		kind: 'neither',
		chip: 'bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-300',
		dot: 'bg-green-600',
	},
	polarising: {
		label: 'Polarising',
		meaning: 'Their own audience loves them, but what they post is the kind of thing sponsors and platforms act on.',
		kind: 'external',
		chip: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300',
		dot: 'bg-amber-500',
	},
	eroding: {
		label: 'Eroding',
		meaning: 'Nothing risky in what they post, but their own followers are turning on them.',
		kind: 'internal',
		chip: 'bg-orange-100 text-orange-800 dark:bg-orange-500/15 dark:text-orange-300',
		dot: 'bg-orange-500',
	},
	crisis: {
		label: 'In crisis',
		meaning: 'What they post would worry a sponsor, and their own followers are against them too.',
		kind: 'both',
		chip: 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300',
		dot: 'bg-red-600',
	},
	contested: {
		label: 'Contested',
		meaning: 'The people replying are split down the middle. Which way it tips decides how much trouble this is.',
		kind: 'undecided',
		chip: 'bg-brand-100 text-brand-800 dark:bg-brand-500/15 dark:text-brand-300',
		dot: 'bg-brand-500',
	},
	unknown: {
		label: 'Not enough signal',
		meaning: 'We are still missing a scan or a pulse.',
		kind: 'none',
		chip: 'bg-muted text-muted-foreground',
		dot: 'bg-input',
	},
};

/** Order for rollups: worst first, so a client's chips read as a triage line. */
export const POSITION_ORDER = ['crisis', 'eroding', 'polarising', 'contested', 'solid', 'unknown'];

/** Stated once, on the card, so nobody has to guess the rule. */
export const STANDING_RULE = 'How this is worked out: a scan score of 60 or more counts as risky. We need at least 30 replies that take a side before we call it - with them at 35% against or less, against them at 50% or more.';

export const positionMeta = (key) => POSITION_META[key] || POSITION_META.unknown;
